import { BaseHTMLElement, customElement, onEvent } from 'dom-native';

import { DEFAULT_ATTACHMENT_POLICY, normalizeAttachmentPolicy } from '@/constants/attachment-policy';
import { HelpAgentAttachmentStore } from '@/controllers/help-agent-attachment-store';
import { DEFAULT_QUICK_PROMPTS } from '@/constants/quick-prompts';
import { HelpAgentGalleryController } from '@/controllers/help-agent-gallery-controller';
import { HelpAgentMessageStore } from '@/controllers/help-agent-message-store';
import { HelpAgentSubmissionController } from '@/controllers/help-agent-submission-controller';
import { HelpAgentToastManager } from '@/controllers/help-agent-toast-manager';
import { ChatWebSocket } from '@/services/chat-websocket';
import type {
  HelpAgentAttachmentPolicy,
  HelpAgentAttachmentPreview,
  HelpAgentSubmitHandler,
} from '@/types/attachments';
import type { ChatConfig, ChatImage, ConnectionStatus, ServerMessage, ToastType } from '@/types/chat';
import type { HelpAgentStreamCleanup, HelpAgentStreamHandler } from '@/types/public';
import type { HelpCenterFooterLinks } from '@/utils/help-center-links';
import { DEFAULT_HELP_CENTER_FOOTER_LINKS } from '@/utils/help-center-links';

import './help-agent-composer';
import './help-agent-header';
import './help-agent-image-gallery-modal';
import './help-agent-message-list';
import './help-agent-session-info';
import './help-agent-toast-stack';
import { HelpAgentViewAdapter } from './help-agent-view-adapter';

const HOST_STREAM_ERROR_MESSAGE = 'An error occurred while receiving the response. Please try again.';

@customElement('help-agent-app')
export class HelpAgentApp extends BaseHTMLElement {
  private _config: ChatConfig | null = null;
  private _authToken: string | null = null;
  private _quickPrompts: readonly string[] = DEFAULT_QUICK_PROMPTS;
  private _footerLinks: HelpCenterFooterLinks = DEFAULT_HELP_CENTER_FOOTER_LINKS;
  private _notificationsEnabled = false;
  private _attachmentPolicy: HelpAgentAttachmentPolicy = DEFAULT_ATTACHMENT_POLICY;
  private _submitHandler: HelpAgentSubmitHandler | null = null;
  private _streamHandler: HelpAgentStreamHandler | null = null;
  private _connectionStatus: ConnectionStatus = 'disconnected';
  private _composerLocked = false;
  private _activeRequestEntityId: string | null = null;
  private _activeHostStreamEntityId: string | null = null;
  private _infoOpen = false;
  private _expanded = false;
  private _hasConnected = false;
  private _transport: ChatWebSocket | null = null;
  private readonly _activeStreamCleanups = new Map<string, HelpAgentStreamCleanup>();
  private readonly _streamsAwaitingCleanup = new Set<string>();
  private readonly _streamsPendingTeardown = new Set<string>();
  private _needsDeferredTransportRebind = false;

  private readonly _attachmentStore = new HelpAgentAttachmentStore();
  private readonly _messageStore = new HelpAgentMessageStore();
  private readonly _submissionController = new HelpAgentSubmissionController();
  private readonly _view = new HelpAgentViewAdapter(this);
  private readonly _toastManager = new HelpAgentToastManager({
    onChange: (toasts) => this._view.setToasts(toasts),
  });
  private readonly _galleryController = new HelpAgentGalleryController();

  init(): void {
    super.init();
    if (!this.hasAttribute('data-test')) {
      this.setAttribute('data-test', 'help-widget');
    }
    this.innerHTML = `
      <div class="help-agent-shell" data-test="help-widget-shell">
        <div class="help-agent-shell-header" data-test="help-widget-shell-header">
          <help-agent-header data-test="help-widget-header"></help-agent-header>
        </div>
        <div class="help-agent-shell-content" data-test="help-widget-shell-content">
          <help-agent-session-info data-test="help-widget-session-info"></help-agent-session-info>
          <help-agent-message-list data-test="help-widget-message-list-host"></help-agent-message-list>
        </div>
        <div class="help-agent-shell-footer" data-test="help-widget-shell-footer">
          <help-agent-composer data-test="help-widget-composer-host"></help-agent-composer>
        </div>
      </div>
      <help-agent-toast-stack data-test="help-widget-toast-stack"></help-agent-toast-stack>
      <help-agent-image-gallery-modal data-test="help-widget-gallery-modal-host"></help-agent-image-gallery-modal>
    `;

    if (typeof customElements !== 'undefined' && typeof customElements.upgrade === 'function') {
      customElements.upgrade(this);
    }
  }

  postDisplay(): void {
    requestAnimationFrame(() => this.syncAll());
  }

  disconnectedCallback(): void {
    this.cleanupActiveStreams();
    this._transport?.disconnect();
    this._attachmentStore.destroy();
    this._messageStore.destroy();
    this._toastManager.destroy();
    super.disconnectedCallback();
  }

  get sessionId(): string {
    return this._config?.sessionId || '';
  }

  set authToken(token: string | null) {
    this.setAuthToken(token);
  }

  set token(token: string | null) {
    this.setAuthToken(token);
  }

  get token(): string | null {
    return this._authToken;
  }

  set config(config: ChatConfig) {
    this.setRuntimeConfig(config);
  }

  get config(): ChatConfig | null {
    return this._config;
  }

  set quickPrompts(prompts: readonly string[] | null | undefined) {
    this.setQuickPrompts(prompts);
  }

  get quickPrompts(): readonly string[] {
    return this._quickPrompts;
  }

  set footerLinks(links: HelpCenterFooterLinks | null | undefined) {
    this.setFooterLinks(links);
  }

  get footerLinks(): HelpCenterFooterLinks {
    return { ...this._footerLinks };
  }

  set attachmentPolicy(policy: Partial<HelpAgentAttachmentPolicy> | HelpAgentAttachmentPolicy | null | undefined) {
    this.setAttachmentPolicy(policy);
  }

  get attachmentPolicy(): HelpAgentAttachmentPolicy {
    return { ...this._attachmentPolicy };
  }

  get attachments(): readonly HelpAgentAttachmentPreview[] {
    return this._attachmentStore.snapshot;
  }

  set submitHandler(handler: HelpAgentSubmitHandler | null) {
    this._submitHandler = handler;
  }

  get submitHandler(): HelpAgentSubmitHandler | null {
    return this._submitHandler;
  }

  set streamHandler(handler: HelpAgentStreamHandler | null) {
    this._streamHandler = handler;

    if (this.initialized) {
      this._bindTransport();
    }
  }

  get streamHandler(): HelpAgentStreamHandler | null {
    return this._streamHandler;
  }

  set notificationsEnabled(enabled: boolean) {
    this.setNotificationsEnabled(enabled);
  }

  get notificationsEnabled(): boolean {
    return this._notificationsEnabled;
  }

  get expanded(): boolean {
    return this._expanded;
  }

  focusInput(): void {
    requestAnimationFrame(() => this._view.focusComposerInput());
  }

  clearAttachments(): void {
    this._attachmentStore.clear();
    this._view.setAttachments(this._attachmentStore.snapshot);
  }

  notify(message: string, type: ToastType): void {
    this.dispatchEvent(new CustomEvent('help-agent:notify', {
      bubbles: true,
      composed: true,
      detail: { message, type },
    }));

    if (!this._notificationsEnabled) {
      return;
    }

    this._toastManager.push(message, type);
  }

  setRuntimeConfig(config: ChatConfig, options?: { preserveActiveRequest?: boolean }): void {
    const previousSessionId = this._config?.sessionId;
    this._config = config;
    const preserveActiveRequest = options?.preserveActiveRequest ?? this._composerLocked;
    const sessionChanged = Boolean(previousSessionId && previousSessionId !== config.sessionId);

    if (sessionChanged) {
      if (preserveActiveRequest) {
        this._needsDeferredTransportRebind = true;
      } else {
        this._needsDeferredTransportRebind = false;
        this.cleanupActiveStreams();
        this.resetSessionUi(false);
      }
    }

    if (this.initialized) {
      if (!sessionChanged || !preserveActiveRequest) {
        this._bindTransport();
      }
      this.syncAll();
    }
  }

  setAuthToken(token: string | null): void {
    this._authToken = token;

    if (this.initialized) {
      this._transport?.setAuthToken(token);
    }
  }

  setQuickPrompts(prompts: readonly string[] | null | undefined): void {
    this._quickPrompts = prompts && prompts.length > 0
      ? [...prompts]
      : DEFAULT_QUICK_PROMPTS;

    if (this.initialized) {
      this._view.setQuickPrompts(this._quickPrompts);
    }
  }

  setFooterLinks(links: HelpCenterFooterLinks | null | undefined): void {
    this._footerLinks = {
      ...(links ?? DEFAULT_HELP_CENTER_FOOTER_LINKS),
    };

    if (this.initialized) {
      this._view.setFooterLinks(this._footerLinks);
    }
  }

  setNotificationsEnabled(enabled: boolean): void {
    const nextValue = Boolean(enabled);
    if (this._notificationsEnabled === nextValue) {
      return;
    }

    this._notificationsEnabled = nextValue;

    if (!nextValue) {
      this._toastManager.clear();
    }
  }

  setAttachmentPolicy(policy: Partial<HelpAgentAttachmentPolicy> | HelpAgentAttachmentPolicy | null | undefined): void {
    this._attachmentPolicy = normalizeAttachmentPolicy(policy);
    this._attachmentStore.setPolicy(this._attachmentPolicy);

    if (this._attachmentPolicy.maxFiles <= 0) {
      this._attachmentStore.clear();
    }

    if (this.initialized) {
      this._view.setAttachments(this._attachmentStore.snapshot);
      this._view.setAttachmentsEnabled(this._attachmentPolicy.maxFiles > 0);
      this._view.setAttachmentAccept(this._attachmentStore.accept);
    }
  }

  expand(): void {
    this.setExpandedState(true, 'api');
  }

  collapse(): void {
    this.setExpandedState(false, 'api');
  }

  toggleExpanded(): void {
    this.setExpandedState(!this._expanded, 'api');
  }

  private syncAll(): void {
    this._view.sync({
      expanded: this._expanded,
      sessionInfo: {
        open: this._infoOpen,
        sessionId: this.sessionId,
        status: this._connectionStatus,
      },
      quickPrompts: this._quickPrompts,
      messages: this._messageStore.snapshot,
      footerLinks: this._footerLinks,
      attachments: this._attachmentStore.snapshot,
      attachmentsEnabled: this._attachmentPolicy.maxFiles > 0,
      attachmentAccept: this._attachmentStore.accept,
      composerLocked: this._composerLocked,
      connectionStatus: this._connectionStatus,
      toasts: this._toastManager.snapshot,
      gallery: this._galleryController.state,
    });
  }

  private setExpandedState(nextExpanded: boolean, source: 'api' | 'header'): void {
    if (this._expanded === nextExpanded) {
      return;
    }

    this._expanded = nextExpanded;
    this.syncAll();
    this.emitExpandedChange(source);
  }

  private emitExpandedChange(source: 'api' | 'header'): void {
    this.dispatchEvent(new CustomEvent('help-agent:expanded-change', {
      bubbles: true,
      composed: true,
      detail: { expanded: this._expanded, source },
    }));
  }

  private _bindTransport(): void {
    if (!this._config) {
      this.cleanupActiveStreams();
      this._transport?.disconnect();
      this._transport = null;
      this.handleConnectionStatus('disconnected');
      return;
    }

    if (this._streamHandler) {
      this._transport?.disconnect();
      this._transport = null;
      this.handleConnectionStatus('connected');
      return;
    }

    if (!this._config.wsUrl) {
      this._transport?.disconnect();
      this._transport = null;
      this.handleConnectionStatus('disconnected');
      return;
    }

    this._transport?.disconnect();
    this._transport = new ChatWebSocket({
      wsUrl: this._config.wsUrl,
      authToken: this._authToken,
      onMessage: (message) => this.handleServerMessage(message),
      onStatusChange: (status) => this.handleConnectionStatus(status),
      onPermanentDisconnect: () => this.notify('Failed to connect to the server', 'error'),
    });
    this._transport.connect();
  }

  private handleConnectionStatus(status: ConnectionStatus): void {
    const previousStatus = this._connectionStatus;
    this._connectionStatus = status;
    this._view.setSessionInfoState({
      open: this._infoOpen,
      sessionId: this.sessionId,
      status,
    });
    this._view.setConnectionStatus(status);

    if (status === 'connected') {
      if (this._hasConnected && previousStatus !== 'connected') {
        this.notify('Connection restored', 'success');
      }
      this._hasConnected = true;
      return;
    }

    if (status === 'disconnected' && previousStatus === 'connected') {
      this.notify('Connection lost. Reconnecting...', 'warning');
    }
  }

  private async handleSend(text: string): Promise<void> {
    if (!this._config || this._composerLocked) {
      return;
    }

    const question = text.trim();
    const attachments = this._attachmentStore.snapshot;

    if (!question && attachments.length === 0) {
      return;
    }

    if (attachments.length > 0 && !this._submitHandler) {
      this.notify('Attachments require a host submit handler or backend support.', 'warning');
      return;
    }

    this.setComposerLocked(true);
    const loadingId = this._messageStore.queueOutgoingQuestion(question, attachments);
    this._view.setMessages(this._messageStore.snapshot);
    let submittedEntityId: string | null = null;

    try {
      const submission = await this._submissionController.submit({
        config: this._config,
        question,
        authToken: this._authToken,
        attachments,
        submitHandler: this._submitHandler,
      });

      submittedEntityId = submission.entityId;
      this._activeRequestEntityId = submission.entityId;

      if (submission.sessionId && submission.sessionId !== this._config.sessionId) {
        this.setRuntimeConfig({
          ...this._config,
          sessionId: submission.sessionId,
        }, { preserveActiveRequest: true });
      }

      this._messageStore.markPending(submission.entityId, loadingId);
      this._view.clearComposerDraft();
      this.clearAttachments();

      if (this._streamHandler) {
        this._activeHostStreamEntityId = submission.entityId;
        await this.startHostStream(submission.entityId);
      }
    } catch (error) {
      if (submittedEntityId !== null && !this._messageStore.hasPendingEntity(submittedEntityId)) {
        return;
      }

      this._activeRequestEntityId = null;
      this._activeHostStreamEntityId = null;
      this.setComposerLocked(false);
      this.applyDeferredTransportRebind();
      const errorDetails = this._submissionController.getErrorDetails(error);

      this._messageStore.rejectPending(loadingId, errorDetails.inlineMessage);
      this._view.setMessages(this._messageStore.snapshot);
      this.notify(errorDetails.notificationMessage, 'error');
    }
  }

  private handleServerMessage(message: ServerMessage): void {
    const shouldCompleteActiveRequest = Boolean(
      message.response
      && message.entity_id === this._activeRequestEntityId
    );

    this._messageStore.applyServerMessage(message);
    this._view.setMessages(this._messageStore.snapshot);

    if (shouldCompleteActiveRequest) {
      this.teardownHostStream(message.entity_id);
      this.finishActiveRequest(message.entity_id);
    }
  }

  private async startHostStream(entityId: string): Promise<void> {
    if (!this._streamHandler || !this._config) {
      return;
    }

    const existingCleanup = this._activeStreamCleanups.get(entityId);
    existingCleanup?.();
    this._activeStreamCleanups.delete(entityId);
    this._streamsAwaitingCleanup.add(entityId);

    let cleanup: HelpAgentStreamCleanup | void;
    try {
      cleanup = await this._streamHandler({
        config: this._config,
        token: this._authToken,
        entityId,
        onMessage: (message) => this.handleServerMessage(message),
        onError: (error) => this.handleStreamError(entityId, error),
        onStatusChange: (status) => this.handleHostStreamStatus(entityId, status),
      });
    } catch (error) {
      this._streamsAwaitingCleanup.delete(entityId);
      this._streamsPendingTeardown.delete(entityId);
      throw error;
    }

    this._streamsAwaitingCleanup.delete(entityId);

    if (cleanup) {
      if (this._streamsPendingTeardown.delete(entityId)) {
        cleanup();
        return;
      }

      this._activeStreamCleanups.set(entityId, cleanup);
      return;
    }

    this._streamsPendingTeardown.delete(entityId);
  }

  private handleHostStreamStatus(entityId: string, status: ConnectionStatus): void {
    if (status === 'disconnected') {
      const shouldRejectPendingResponse = entityId === this._activeRequestEntityId
        && this._messageStore.hasPendingEntity(entityId);
      this.teardownHostStream(entityId);

      if (shouldRejectPendingResponse) {
        this._messageStore.rejectPendingEntity(entityId, HOST_STREAM_ERROR_MESSAGE);
        this._view.setMessages(this._messageStore.snapshot);
        this.notify('Response stream error', 'error');
      }

      this.finishActiveRequest(entityId);
    }

    if (status === 'disconnected' && this._streamHandler && this._config) {
      this.handleConnectionStatus('connected');
      return;
    }

    this.handleConnectionStatus(status);
  }

  private handleStreamError(entityId: string, error: unknown): void {
    const message = error instanceof Error && error.message
      ? error.message
      : HOST_STREAM_ERROR_MESSAGE;
    const shouldRejectPendingResponse = this._messageStore.hasPendingEntity(entityId);

    this.teardownHostStream(entityId);
    this.finishActiveRequest(entityId);

    if (!shouldRejectPendingResponse) {
      return;
    }

    this._messageStore.rejectPendingEntity(entityId, message);
    this._view.setMessages(this._messageStore.snapshot);
    this.notify('Response stream error', 'error');
  }

  private cleanupActiveStreams(): void {
    const entityIds = new Set<string>([
      ...this._activeStreamCleanups.keys(),
      ...this._streamsAwaitingCleanup,
    ]);
    for (const entityId of entityIds) {
      this.teardownHostStream(entityId);
    }

    this._activeStreamCleanups.clear();
  }

  private teardownHostStream(entityId: string): void {
    const cleanup = this._activeStreamCleanups.get(entityId);
    this._activeStreamCleanups.delete(entityId);

    if (this._activeHostStreamEntityId === entityId) {
      this._activeHostStreamEntityId = null;
    }

    if (cleanup) {
      cleanup();
      return;
    }

    if (this._streamsAwaitingCleanup.has(entityId)) {
      this._streamsPendingTeardown.add(entityId);
    }
  }

  private finishActiveRequest(entityId: string): void {
    if (entityId !== this._activeRequestEntityId) {
      return;
    }

    this._activeRequestEntityId = null;
    this.setComposerLocked(false);
    this.applyDeferredTransportRebind();
  }

  private applyDeferredTransportRebind(): void {
    if (!this._needsDeferredTransportRebind) {
      return;
    }

    this._needsDeferredTransportRebind = false;
    if (!this.initialized || !this.isConnected) {
      return;
    }

    this._bindTransport();
    this.syncAll();
  }

  private resetSessionUi(preserveActiveRequest = false): void {
    this.cleanupActiveStreams();
    if (!preserveActiveRequest) {
      this._activeRequestEntityId = null;
      this._activeHostStreamEntityId = null;
      this.setComposerLocked(false);
      this._messageStore.reset();
    }
    this._attachmentStore.clear();
    this._galleryController.reset();
    this._toastManager.clear();
    this._infoOpen = false;
    if (!preserveActiveRequest) {
      this._view.setMessages(this._messageStore.snapshot);
    }
    this._view.setGalleryState(this._galleryController.state);
    this._view.setExpanded(this._expanded);
    this._view.clearComposerDraft();
    this._view.setAttachments(this._attachmentStore.snapshot);
    this._view.setSessionInfoState({
      open: this._infoOpen,
      sessionId: this.sessionId,
      status: this._connectionStatus,
    });
  }

  private setComposerLocked(locked: boolean): void {
    if (this._composerLocked === locked) {
      return;
    }

    this._composerLocked = locked;
    this._view.setComposerLocked(locked);
  }

  private async copySessionId(): Promise<void> {
    if (!this.sessionId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.sessionId);
      this.notify('Session ID copied', 'success');
    } catch {
      this.notify('Unable to copy session ID', 'error');
    }
  }

  private updateGalleryIndex(delta: number): void {
    this._view.setGalleryState(this._galleryController.shift(delta));
  }

  private requestNewChat(source: 'header'): void {
    const detail = {
      source,
      currentSessionId: this.sessionId,
    };

    this.dispatchEvent(new CustomEvent('help-agent:new-chat', {
      bubbles: true,
      composed: true,
      detail,
    }));
  }

  private emitCloseRequest(source: 'header'): void {
    this.dispatchEvent(new CustomEvent('help-agent:close', {
      bubbles: true,
      composed: true,
      detail: { source },
    }));
  }

  @onEvent('help-agent:toggle-info', 'help-agent-header')
  onToggleInfo(): void {
    this._infoOpen = !this._infoOpen;
    this._view.setSessionInfoState({
      open: this._infoOpen,
      sessionId: this.sessionId,
      status: this._connectionStatus,
    });
  }

  @onEvent('help-agent:new-chat-intent', 'help-agent-header')
  onNewChat(event: Event): void {
    event.stopPropagation();
    this.requestNewChat('header');
  }

  @onEvent('help-agent:toggle-expand', 'help-agent-header')
  onToggleExpand(): void {
    this.setExpandedState(!this._expanded, 'header');
  }

  @onEvent('help-agent:close-intent', 'help-agent-header')
  onClose(event: Event): void {
    event.stopPropagation();
    this.emitCloseRequest('header');
  }

  @onEvent('help-agent:copy-session', 'help-agent-session-info')
  onCopySession(): void {
    void this.copySessionId();
  }

  @onEvent('help-agent:send', 'help-agent-composer')
  onSend(event: CustomEvent<{ text: string }>): void {
    void this.handleSend(event.detail.text);
  }

  @onEvent('help-agent:select-prompt', 'help-agent-message-list')
  onSelectPrompt(event: CustomEvent<{ text: string }>): void {
    void this.handleSend(event.detail.text);
  }

  @onEvent('help-agent:files-selected', 'help-agent-composer')
  onFilesSelected(event: CustomEvent<{ files: File[] }>): void {
    if (this._composerLocked) {
      return;
    }

    const result = this._attachmentStore.addFiles(event.detail.files);
    this._view.setAttachments(this._attachmentStore.snapshot);

    for (const rejection of result.rejected) {
      this.notify(`${rejection.fileName}: ${rejection.reason}`, 'warning');
    }
  }

  @onEvent('help-agent:remove-attachment', 'help-agent-composer')
  onRemoveAttachment(event: CustomEvent<{ id: string }>): void {
    if (this._composerLocked) {
      return;
    }

    this._attachmentStore.remove(event.detail.id);
    this._view.setAttachments(this._attachmentStore.snapshot);
  }

  @onEvent('help-agent:open-gallery', 'help-agent-message-list')
  onOpenGallery(event: CustomEvent<{ images: ChatImage[]; startIndex: number }>): void {
    this._view.setGalleryState(this._galleryController.open(event.detail.images, event.detail.startIndex));
  }

  @onEvent('help-agent:open-gallery', 'help-agent-composer')
  onOpenComposerGallery(event: CustomEvent<{ images: ChatImage[]; startIndex: number }>): void {
    this._view.setGalleryState(this._galleryController.open(event.detail.images, event.detail.startIndex));
  }

  @onEvent('help-agent:gallery-close', 'help-agent-image-gallery-modal')
  onCloseGallery(): void {
    this._view.setGalleryState(this._galleryController.close());
  }

  @onEvent('help-agent:gallery-prev', 'help-agent-image-gallery-modal')
  onGalleryPrev(): void {
    this.updateGalleryIndex(-1);
  }

  @onEvent('help-agent:gallery-next', 'help-agent-image-gallery-modal')
  onGalleryNext(): void {
    this.updateGalleryIndex(1);
  }
}
